import { LocalVideoTrack } from 'livekit-client'

import { BackgroundProcessor, LongpipeBackgroundProcessor } from '@/src/processor'
import { isBackgroundEffectAvailable } from '@/src/lib/detectBackgroundPipeline'
import { BgEffect } from '@/src/lib/types'
import { setBackgroundModelLoading } from './useBackgroundModelLoading'

/** Последовательные переключения исключают два worker на одной камере при повторных React-эффектах. */
const pendingChanges = new WeakMap<LocalVideoTrack, Promise<void>>()

/** Применяет фон к камере и превью через единый процессор LiveKit во всех браузерах.
 * @param track — локальный видеотрек камеры.
 * @param currentBgEffect — выбранный эффект или отключение обработки.
 * @param previewVideo — элемент превью до входа в комнату.
 */
export async function useToggleTrackProcessor(
  track: LocalVideoTrack | undefined,
  currentBgEffect: BgEffect | null,
  previewVideo?: HTMLVideoElement | null,
) {
  if (!track) return

  const change = (pendingChanges.get(track) ?? Promise.resolve()).then(async () => {
    const processor = track.getProcessor()

    if (!currentBgEffect || currentBgEffect.type === 'none') {
      if (processor instanceof LongpipeBackgroundProcessor) {
        await track.stopProcessor()
      }
    } else if (isBackgroundEffectAvailable()) {
      if (processor instanceof LongpipeBackgroundProcessor) {
        await processor.update(currentBgEffect.options, currentBgEffect.type)
      } else {
        const nextProcessor = BackgroundProcessor(currentBgEffect.options, currentBgEffect.type)

        nextProcessor.onLoadingChange = (loading) => setBackgroundModelLoading(track, loading)

        // После потери GPU или падения worker возвращаем камеру, не оставляя зрителям замерший кадр.
        nextProcessor.onFatalError = () => {
          const restore = (pendingChanges.get(track) ?? Promise.resolve()).then(async () => {
            if (track.getProcessor() === nextProcessor) {
              await track.stopProcessor()
            }
          }).catch((error) => console.warn('Не удалось восстановить исходную камеру', error))

          pendingChanges.set(track, restore)
        }

        try {
          await track.setProcessor(nextProcessor, true)
        } catch (error) {
          await nextProcessor.destroy()

          throw error
        }
      }
    }

    if (previewVideo) {
      // attach регистрирует элемент в LiveKit: SDK сам обновит превью при смене и отключении процессора.
      track.attach(previewVideo)
    }
  }).catch((error) => console.warn('Не удалось применить эффект фона', error))

  pendingChanges.set(track, change)
  await change

  return track
}
